import { Close, NavigateBefore, NavigateNext } from "@mui/icons-material"
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { JsonView } from "react-json-view-lite"
import { Sst2Reservation } from "shared/sstv2Types"
import { useGetSstv2ReservationsQuery } from "src/features/partners/partnersApi"

const STATUS_COLORS: Record<
  string,
  "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"
> = {
  DRAFT: "default",
  CONFIRMED: "info",
  DELIVERED: "success",
  EXPIRED: "warning",
  CANCELLED: "error",
  CLOSED: "default",
  DELETED: "error",
}

export const SaSstv2Reservations: FC = () => {
  const { t } = useTranslation()

  const [page, setPage] = useState(1)
  const [count] = useState(20)

  const qReservations = useGetSstv2ReservationsQuery({ page, count })
  const reservations = qReservations.data?.data || []

  const [previewReservation, setPreviewReservation] =
    useState<Sst2Reservation | null>(null)

  const handleRowClick = useCallback(
    (reservation: Sst2Reservation) => {
      setPreviewReservation(reservation)
    },
    [],
  )

  return (
    <Stack data-ui="UI-SL2W-237" direction="column" spacing={2} width="100%">
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body1" fontWeight="bold">
          {`${t("reservations")} (${t("page")} ${page})`}
        </Typography>
        {(qReservations.isLoading || qReservations.isFetching) && (
          <CircularProgress size={18} />
        )}
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>ID</strong>
              </TableCell>
              <TableCell>
                <strong>{t("status")}</strong>
              </TableCell>
              <TableCell>
                <strong>Door ID</strong>
              </TableCell>
              <TableCell>
                <strong>Expiration</strong>
              </TableCell>
              <TableCell>
                <strong>Reception Code</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reservations.map((reservation) => (
              <TableRow
                key={reservation.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => handleRowClick(reservation)}
              >
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {reservation.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={reservation.status}
                    size="small"
                    color={STATUS_COLORS[reservation.status] || "default"}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {reservation.door_id || "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {reservation.expiration || "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {reservation.reception_code || "-"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {!qReservations.isLoading && reservations.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No data
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton
          size="small"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
        >
          <NavigateBefore />
        </IconButton>
        <Typography variant="body2">{`${t("page")} ${page}`}</Typography>
        <IconButton
          size="small"
          onClick={() => setPage((p) => p + 1)}
          disabled={reservations.length < count}
        >
          <NavigateNext />
        </IconButton>
      </Stack>

      {/* Detail Drawer */}
      <Drawer data-ui="UI-SL2W-238"
        anchor="right"
        open={!!previewReservation}
        onClose={() => setPreviewReservation(null)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "80vw",
            maxWidth: "1500px",
            maxHeight: "calc(100vh - 64px)",
            height: "calc(100vh - 64px)",
            paddingTop: "64px",
          },
        }}
      >
        <Stack direction="column" spacing={1} p={2}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Typography variant="body1" fontWeight="bold">
                {previewReservation?.id}
              </Typography>
              <Chip
                label={previewReservation?.status}
                size="small"
                color={
                  STATUS_COLORS[previewReservation?.status || ""] || "default"
                }
              />
            </Stack>
            <IconButton onClick={() => setPreviewReservation(null)}>
              <Close />
            </IconButton>
          </Stack>
          <Divider />
          <Box sx={{ overflow: "auto", maxHeight: "calc(100vh - 160px)" }}>
            {previewReservation && (
              <JsonView data={previewReservation as any} />
            )}
          </Box>
        </Stack>
      </Drawer>
    </Stack>
  )
}

export default SaSstv2Reservations
